import 'react-medium-image-zoom/dist/styles.css';

import Carousel from '@brainhubeu/react-carousel';
import ProductVideo from '@common/components/ProductVideo';
import ZoomLens from '@common/components/ZoomLens';
import { getYoutubeVideoThumbnail, isMobile, matchYoutubeUrlRegex } from '@common/utils';
import { getLambdaLink } from '@common/utils';
// import { ReactComponent as CarouselArrowRight } from '@common/icons/CarouselArrowRight.svg';
import { ReactComponent as CarouselArrowLeft } from '@images/ArrowLeft.svg';
import { ReactComponent as CarouselArrowRight } from '@images/ArrowRight.svg';
import { PlayButtonIcon } from '@instamojo/icons';
import Image from '@lib/Image';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

export interface GalleryProps {
  className?: string;
  isZoomEnabled: boolean;
  images: string[];
  quickView?: boolean;
  imageDescription?: string;
  productVideoUrl?: string;
  productVideoEnabled?: boolean;
}

const Gallery: React.FunctionComponent<GalleryProps> = ({
  images = [],
  quickView,
  imageDescription = '',
  productVideoUrl,
  productVideoEnabled,
}) => {
  const [selectedImage, setSelectedImage] = useState(images[0] || '');
  const [selectedVideo, setSelectedVideo] = useState('');
  const [backgroundPosition, setBackgroundPosition] = useState('0% 0%');
  const [mouseEnter, setMouseEnter] = useState(false);
  const [lensPos, setLensPos] = useState({ left: 0, top: 0 });

  useEffect(() => {
    setSelectedImage(images[0]);
  }, [images]);
  const handleMouseMove = (e) => {
    const { left, top, width, height } = imageRef.current.getBoundingClientRect();
    const minX = Math.min(e.pageX - left, width - 100);
    const minY = Math.min(e.pageY - top, height - 100);
    setLensPos({
      left: minX,
      top: minY,
    });
    setBackgroundPosition(`-${minX * 4}px -${minY * 4}px`);
  };
  const imageRef = useRef(null);
  const divRef = useRef(null);
  const zoomedImage = selectedImage.replace(/(.*)(_image\d*_)(.*)/, '$1$2zoom_$3');

  return (
    <div className={classnames('w-full flex flex-wrap md:w-400 justify-center')} ref={divRef}>
      <div
        className={classnames('relative text-center flex md:h-348 md:w-348 justify-center md:mb-18', {
          ' w-366 h-348 bannerBorder md:border-0 rounded-lg md:rounded-none py-18 px-33 md:p-0': !quickView,
          ' w-full h-258 max-w-340 bannerBorder rounded-lg': quickView,
        })}
      >
        {' '}
        {selectedVideo ? (
          <ProductVideo
            productVideoUrl={selectedVideo}
            height={isMobile() && quickView ? '258' : isMobile() ? '310' : quickView ? '350' : '348'}
            width={isMobile() && quickView ? '280' : isMobile() ? '310' : quickView ? '350' : '336'}
          />
        ) : (
          <div>
            <figure
              onMouseEnter={() => {
                setMouseEnter(true);
              }}
              onMouseLeave={() => {
                setMouseEnter(false);
              }}
              onMouseMove={handleMouseMove}
              className="w-full h-full"
            >
              <img
                ref={imageRef}
                className={classnames('object-contain md:rounded-3xl w-full h-full')}
                loading={'lazy'}
                src={getLambdaLink({
                  link: selectedImage,
                  width: 0,
                  height: 0,
                })}
                alt={imageDescription}
              />
              {!isMobile() && !quickView ? <ZoomLens lensPos={lensPos} mouseEnter={mouseEnter} /> : null}
            </figure>
            {mouseEnter && !quickView && (
              <div
                className="absolute rounded z-40 overflow-hidden"
                style={{
                  left: 400,
                  top: 0,
                  height: 350,
                  width: 350,
                  backgroundImage: `url('${zoomedImage}'), url('${selectedImage}')`,
                  backgroundPosition,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: `${imageRef.current?.width * 4 || 500}px ${imageRef.current?.height * 4 || 500}px`,
                }}
              ></div>
            )}
          </div>
        )}
      </div>
      {GalleryCarousel(
        images,
        selectedImage,
        setSelectedImage,
        quickView,
        divRef,
        imageDescription,
        productVideoUrl,
        setSelectedVideo,
        getYoutubeVideoThumbnail,
        selectedVideo,
        productVideoEnabled,
      )}
    </div>
  );
};

const GalleryCarousel = (
  imagesGallery = [],
  selectedImage,
  setSelectedImage,
  quickView,
  divRef,
  imageDescription,
  productVideoUrl,
  setSelectedVideo,
  getYoutubeVideoThumbnail,
  selectedVideo,
  productVideoEnabled,
) => {
  const images = [...imagesGallery];
  if (productVideoEnabled) {
    images.push(productVideoUrl);
  }
  return (
    <div
      className={classnames('w-full md:mb-0', {
        ' flex': quickView,
      })}
    >
      {images.length > 1 || productVideoEnabled ? (
        isMobile() ? (
          <div
            className={`flex flex-no-wrap overflow-x-auto custom-scrollbar pt-16 ${
              (images.length > 1 || productVideoEnabled) && 'pb-10'
            }`}
          >
            {images.map((image) => {
              return (
                <>
                  {matchYoutubeUrlRegex(image) ? (
                    <div
                      className="relative"
                      onClick={() => {
                        setSelectedImage('');
                        setSelectedVideo(productVideoUrl);
                      }}
                    >
                      <div className="absolute w-64 h-64 rounded-lg zIndex-999 bg-gray opacity-80 flex items-center justify-center">
                        <PlayButtonIcon size="16" color="#000" />
                      </div>
                      <Image
                        src={getYoutubeVideoThumbnail(productVideoUrl)}
                        alt={imageDescription}
                        className={classnames('object-contain max-w-none rounded-lg h-64 w-64 min-w-64 mr-12', {
                          'border-gallery-selected border': selectedVideo === productVideoUrl,
                          'border-gallery-normal border': selectedVideo !== productVideoUrl,
                        })}
                        noDefaultDimensions
                        noDefaultFit
                      />
                    </div>
                  ) : (
                    <Image
                      src={getLambdaLink({
                        link: image,
                        width: 0,
                        height: 0,
                      })}
                      alt="Other Image"
                      className={classnames('object-contain max-w-none rounded-lg h-64 w-64 min-w-64 mr-12', {
                        'border-gallery-selected border': selectedImage === image,
                        'border-gallery-normal border': selectedImage !== image,
                      })}
                      key={image}
                      onClick={() => {
                        setSelectedImage(image);
                        setSelectedVideo('');
                      }}
                      noDefaultDimensions
                    />
                  )}
                </>
              );
            })}
          </div>
        ) : (
          <Carousel
            arrows={images.length > 3}
            arrowLeft={images.length > 3 ? <CarouselArrowLeft className="w-28 h-28" /> : null}
            arrowRight={images.length > 3 ? <CarouselArrowRight className="w-28 h-28" /> : null}
            addArrowClickHandler
            slidesPerPage={divRef.current?.clientWidth < 360 ? 2 : 3}
          >
            {images.map((image) => {
              return (
                <>
                  {matchYoutubeUrlRegex(image) ? (
                    <div
                      className="relative"
                      onClick={() => {
                        setSelectedImage('');
                        setSelectedVideo(productVideoUrl);
                      }}
                    >
                      <div className="absolute w-full h-full rounded-xl zIndex-999 bg-gray opacity-80 flex items-center justify-center">
                        <PlayButtonIcon size="24" color="#000" />
                      </div>
                      <Image
                        src={getYoutubeVideoThumbnail(productVideoUrl)}
                        alt={imageDescription}
                        className={classnames(
                          'object-contain max-w-none  md:h-108 md:w-108 rounded-xl md:rounded-3xl',
                          {
                            'border-gallery-selected border': selectedVideo === productVideoUrl,
                            'border-gallery-normal border': selectedVideo !== productVideoUrl,
                            'h-100 w-100': !quickView,
                          },
                        )}
                        noDefaultDimensions
                        noDefaultFit
                      />
                    </div>
                  ) : (
                    <Image
                      src={getLambdaLink({
                        link: image,
                        width: 0,
                        height: 0,
                      })}
                      alt={imageDescription}
                      className={classnames('object-contain max-w-none  md:h-108 md:w-108 rounded-xl md:rounded-3xl', {
                        'border-gallery-selected border': selectedImage === image,
                        'border-gallery-normal border': selectedImage !== image,
                        'h-100 w-100': !quickView,
                      })}
                      key={image}
                      onClick={() => {
                        setSelectedImage(image);
                        setSelectedVideo('');
                      }}
                      noDefaultDimensions
                    />
                  )}
                </>
              );
            })}
          </Carousel>
        )
      ) : null}
    </div>
  );
};

export default Gallery;
